import { Trans } from "@lingui/macro";
import { TransProps } from "@lingui/react";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { ISystemState } from "model/system/systemReducer";
import { addNotificationAC } from "model/system/systemActionCreators";
import { numberCurrencyFormat } from "constants/convertToLocalCurrency";
import { InputTextSlim } from "components/UI/InputTextSlim/InputTextSlim";
import {
  USERPOSITIONS,
  POSITION_ALLOW_FULL_CRUD_PRODUCTS,
} from "constants/userPositions";
import {
  TrashIcon,
  PlusCircleIcon,
  MinusCircleIcon,
} from "@heroicons/react/outline";
import {
  dOrdersSetDetailProductAC,
  dOrdersDeleteProductFromCartItemAC,
  dOrdersSetCountForProductInCartItemAC,
} from "model/dashboardOrders/dOrdersActionCreators";

// Type
export type typeProductItem = {
  id: string;
  name: string;
  unit: string;
  count: number;
  index: number;
  price: string;
  marked?: boolean;
  markLabel?: string;
  isDecimalUnits: boolean;
  category?: string | React.ReactElement<TransProps>;
  setOpenChangePrise: React.Dispatch<React.SetStateAction<boolean>>;
  setOpenPopupDeleteMarkedProduct: React.Dispatch<
    React.SetStateAction<boolean>
  >;
};

// Component
export const ProductItem: React.FC<typeProductItem> = ({
  id,
  name,
  unit,
  index,
  count,
  price,
  marked,
  markLabel,
  isDecimalUnits,
  setOpenChangePrise,
  setOpenPopupDeleteMarkedProduct,
}) => {
  const dispatch = useDispatch();
  const position = useSelector(
    (state: { system: ISystemState }) => state.system.userInfo?.position,
  );
  const [validationObject, setValidationObject] = useState({
    validCount: true,
  });

  useEffect(() => {
    if (markLabel) {
      if (count > 1) {
        setValidationObject({ validCount: false });
      } else {
        setValidationObject({ validCount: true });
      }
    }

    // eslint-disable-next-line
  }, []);

  const onMinusClick = () => {
    if (count > 1) {
      dispatch(
        dOrdersSetCountForProductInCartItemAC(
          id,
          Number((count - 1).toFixed(3)),
        ),
      );
    } else {
      onDeleteClickHandler();
    }
  };

  const onPlusClick = () => {
    if (!markLabel) {
      dispatch(
        dOrdersSetCountForProductInCartItemAC(
          id,
          Number((count + 1).toFixed(3)),
        ),
      );
    }
  };

  const onChangePriceHandler = (productId: string) => {
    if (POSITION_ALLOW_FULL_CRUD_PRODUCTS.includes(position as USERPOSITIONS)) {
      setOpenChangePrise(true);
      dispatch(dOrdersSetDetailProductAC(productId));
    } else {
      const id = new Date();
      dispatch(
        addNotificationAC({
          id: id.getTime().toString(),
          message: "ERROR_ACCESS",
        }),
      );
    }
  };

  const onDeleteClickHandler = () => {
    if (marked) {
      dispatch(
        dOrdersSetDetailProductAC({
          id: id,
          index: index,
          markLabel: markLabel,
        } as any),
      );
      setOpenPopupDeleteMarkedProduct(true);
    } else {
      dispatch(dOrdersDeleteProductFromCartItemAC(index));
    }
  };

  const onChangeInput = (event: React.ChangeEvent<HTMLInputElement>) => {
    let value = !isDecimalUnits
      ? Math.floor(+event.target.value)
      : +(event.target.value.length > 0
          ? event.target.value.match(/\d{0,10}.\d{0,3}/)![0]
          : event.target.value);

    if (value < 0) {
      return;
    }

    if (marked && value === 0) {
      onDeleteClickHandler();
    } else {
      dispatch(dOrdersSetCountForProductInCartItemAC(id, value));
    }
  };

  return (
    <tr key={index} className={index % 2 === 0 ? "bg-white" : "bg-gray-50"}>
      <td className="px-2 py-1 whitespace-nowrap text-left text-xs font-medium text-gray-500">
        <div className="flex items-center">{index + 1}</div>
      </td>
      <td className="px-2 py-1 whitespace-nowrap text-left text-sm font-medium text-gray-900">
        <div className="flex-grow items-center">
          <div className="text-base whitespace-normal">{name}</div>
          <div className="text-xxs text-gray-600 ">
            {markLabel && !!markLabel.length && (
              <div className="text-xxs text-gray-600">
                <span>
                  <Trans>Mark label</Trans>:
                </span>{" "}
                <span className="text-xs text-gray-600">{markLabel}</span>
              </div>
            )}
          </div>
        </div>
      </td>
      <td className="w-min px-2 py-1 whitespace-nowrap text-sm text-center font-medium text-gray-900">
        <div className="flex items-center gap-1 w-full justify-end">
          <div
            className={`text-gray-${
              count > 0 ? "400 hover:text-gray-500 cursor-pointer" : "200"
            }`}
            onClick={() => onMinusClick()}
          >
            <MinusCircleIcon className="h-6 w-6" />
          </div>
          <InputTextSlim
            id={id}
            type="number"
            minValue={0}
            value={String(count)}
            inputClassName="w-20 text-center"
            onChange={(e) => onChangeInput(e)}
            maxValue={markLabel ? 1 : undefined}
            stepValue={isDecimalUnits ? 0.001 : 1}
            errorWithoutMessage={!validationObject.validCount || count === 0}
          />
          <div
            className={` ${
              markLabel && count >= 1
                ? "text-gray-200"
                : "text-gray-400 hover:text-gray-500 cursor-pointer"
            }`}
            onClick={() => onPlusClick()}
          >
            <PlusCircleIcon className="h-6 w-6" />
          </div>
        </div>
      </td>
      <td className="w-min px-2 py-1 whitespace-nowrap text-sm text-center font-medium text-gray-900">
        <div className="text-sm text-gray-400">{unit}</div>
      </td>
      <td className="w-min px-2 py-1 whitespace-nowrap text-sm text-right font-medium text-gray-900">
        <div
          onClick={() => onChangePriceHandler(id)}
          className="text-lg font-medium cursor-pointer"
        >
          {numberCurrencyFormat(+price)}
        </div>
      </td>
      <td className="w-min px-2 py-1 whitespace-nowrap text-sm text-center font-medium text-gray-900">
        <div className="text-sm text-gray-400">
          <Trans>sum</Trans>
        </div>
      </td>
      <td className="w-min px-2 py-1 whitespace-nowrap text-sm text-center font-medium text-gray-900">
        <div
          onClick={() => onDeleteClickHandler()}
          className="p-2 flex justify-center cursor-pointer text-gray-300 hover:text-gray-600"
        >
          <TrashIcon className="h-6 w-6" />
        </div>
      </td>
    </tr>
  );
};
